// ============================================
// emissive 通道仲裁器（阶段3｜3.4）：告警闪烁 > 选中高亮 > 悬停 > 数据变色
// 背景：选中高亮（ModelViewer applyHighlight）、告警闪烁（阶段6）、数据变色
//（useBindingApply config.emissive）共用材质 emissive 通道，历史实现相互覆盖。
// 本模块收集各来源的"期望态"，统一按优先级计算最终 emissive/emissiveIntensity。
// ============================================

import { create } from 'zustand';

export interface EmissiveVisualState {
  /** 告警中（阶段6 告警闪烁接入；现在预留字段） */
  alarm: boolean;
  /** 选中高亮（3D 点击选中） */
  selected: boolean;
  /** 悬停高亮 */
  hovered: boolean;
  /** 2026-09-14：悬停强度系数（屏幕占比自适应——占比大→衰减，0=不染色；缺省 1） */
  hoverScale?: number;
  /** 数据绑定驱动的 emissive 色（useBindingApply config.emissive） */
  dataEmissive: string | null;
  /** 数据绑定驱动的 emissiveIntensity */
  dataIntensity: number | null;
}

export const EMPTY_VISUAL_STATE: EmissiveVisualState = {
  alarm: false,
  selected: false,
  hovered: false,
  dataEmissive: null,
  dataIntensity: null,
};

export interface EmissiveResult {
  emissive: string;
  emissiveIntensity: number;
}

/**
 * 优先级仲裁（计算 emissive）：告警 > 悬停 > 数据变色
 * 2026-10-15（P0-⑤ §8.1）：**选中不再整体染色**——改为外轮廓描边（SceneCanvas postprocessing Outline，
 * 见 selection 的 selectedObject），避免大模型全屏蓝色染色。
 */
export function computeEmissive(state: EmissiveVisualState): EmissiveResult {
  if (state.alarm) return { emissive: '#ff4d4f', emissiveIntensity: 0.6 };
  if (state.hovered) return { emissive: '#40a9ff', emissiveIntensity: 0.05 * (state.hoverScale ?? 1) };
  if (state.dataEmissive && state.dataIntensity != null) {
    return { emissive: state.dataEmissive, emissiveIntensity: state.dataIntensity };
  }
  return { emissive: '#000000', emissiveIntensity: 0 };
}

/** 全量状态（nodeId → 各来源期望态） */
interface EmissiveArbiterState {
  states: Record<string, EmissiveVisualState>;
  /** 合并某来源的期望态（null 值忽略，不覆盖既有） */
  update: (nodeId: string, patch: Partial<EmissiveVisualState>) => void;
  getState: (nodeId: string) => EmissiveVisualState;
  clear: () => void;
}

export const useEmissiveArbiterStore = create<EmissiveArbiterState>((set, get) => ({
  states: {},
  update: (nodeId, patch) =>
    set((s) => ({
      states: {
        ...s.states,
        [nodeId]: { ...(s.states[nodeId] || EMPTY_VISUAL_STATE), ...patch },
      },
    })),
  getState: (nodeId) => get().states[nodeId] || EMPTY_VISUAL_STATE,
  clear: () => set({ states: {} }),
}));